import { Component, type ChangeEvent, type FormEvent } from "react";
import {
    Dropdown,
    type IDropdownProps,
    type IDropdownOption,
    SelectableOptionMenuItemType,
    type ISelectableOption,
    SearchBox,
    Async,
    type IDropdownStyles,
    Stack,
    IconButton,
} from "@fluentui/react";
import { type IdProvider, type UUID } from "../../utils/uuid";
import { observer } from "mobx-react";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { clone, omit } from "ramda";

import { store } from "../../store";
import type { ElofleetRepository } from "../../utils/elofleet-repository";
import { defaultPageSize } from "../../utils/constants";
import type { SortDirection } from "../../api";

export type ISearchableDropdownProps<
    TQuery,
    TEntity extends IdProvider,
    TRepository extends ElofleetRepository,
> = {
    readonly repository: TRepository;
    readonly query: TQuery;
    readonly formatEntity: (entity: TEntity) => string;
    readonly selectedKey?: UUID | null;
    readonly selectedKeys?: string[];
    readonly onChange: (value?: UUID | undefined, selected?: boolean) => void;
    readonly hideSearch?: boolean;
    readonly clearable?: boolean;
} & Omit<IDropdownProps, "options" | "onChange" | "selectedKey" | "selectedKeys">;

const dropdownStyles: Partial<IDropdownStyles> = {
    dropdownItemHeader: { padding: "8px", height: "unset" },
};

@observer
export default class SearchableDropdown<
    TQuery extends { pageSize?: number; sortKey?: string; sortDirection?: SortDirection },
    TEntity extends { id: string },
    TRepository extends ElofleetRepository,
> extends Component<ISearchableDropdownProps<TQuery, TEntity, TRepository>> {
    @observable private accessor currentSearch: string | undefined;
    @observable private accessor totalEntityCount: number | undefined;

    constructor(props: ISearchableDropdownProps<TQuery, TEntity, TRepository>) {
        super(props);
        makeObservable(this);

        this.props.repository
            .fetchMetadata({
                pageSize: defaultPageSize,
            })
            .then((metadata) => {
                runInAction(() => {
                    this.totalEntityCount = metadata.totalCount;
                });
            });
    }

    @computed private get entities(): TEntity[] {
        // Use either an empty query or the one provided via props.
        const query: TQuery & { pageSize?: number; search?: string } = this.props.query
            ? clone(this.props.query)
            : ({} as TQuery);
        // Patch the base query with the current search.
        if (this.currentSearch !== undefined) {
            query.search = this.currentSearch;
        }

        query.pageSize = this.totalEntityCount;
        let entities = this.props.repository.byQuery(query as TQuery, {
            offset: 0,
            count: this.totalEntityCount,
        }) as TEntity[];

        // We have to add the currently selected key to the entities, if not already in there.
        if (
            this.props.selectedKey &&
            !entities.some((entity) => entity.id === this.props.selectedKey)
        ) {
            const currentSelection = this.props.repository.mutableCopyById(
                SearchableDropdown.name,
                this.props.selectedKey,
            ) as TEntity | undefined;
            if (currentSelection != null) {
                entities = entities.concat(currentSelection);
            }
        }

        return entities;
    }

    @computed private get options(): IDropdownOption[] {
        return this.entities.map((entity) => ({
            key: entity.id,
            text: this.props.formatEntity(entity),
        }));
    }

    @action.bound private updateSelection(
        event: FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
    ): void {
        // Don't do anything when focus is lost (E.g. escape is pressed).
        if (event.nativeEvent instanceof FocusEvent) {
            return;
        }

        // Propagate the selection state to the parent component.
        if (option !== undefined) {
            this.props.onChange(option.key as UUID, option.selected);
            return;
        }
    }

    @action.bound private clearSelection(_evt: unknown, option?: IDropdownOption): void {
        this.props.onChange?.(option?.key as UUID);
    }

    /**
     * Handle input changes in the actual text field.
     * It's triggered via many different events, which we try to filter.
     *
     * The search should only be updated if the user changes the text.
     */
    @action.bound private updateSearch(_ev?: ChangeEvent<HTMLInputElement>, value?: string): void {
        // Don't update the search if the event contains no new data or the term didn't change.
        if (value === undefined || this.currentSearch === value.trim()) {
            return;
        }

        // Update the search field state.
        if (value.trim() === "") {
            // Remove the current search if we have an empty string.
            // Otherwise we would query the backend with an empty string search.
            this.currentSearch = undefined;

            // Also unselect the currently selected element.
            if (this.props.selectedKey) {
                this.props.onChange(this.props.selectedKey.toString(), false);
            }
        } else {
            this.currentSearch = value.trim();

            const matchingOption = this.options.find((option) => option.text == this.currentSearch);
            if (matchingOption !== undefined) {
                // We have a matching option for the current search term.
                // This should have already been set by a previous event, but that event is sometimes
                // not dispatched for unknown reasons. That's why we just double-tap to be sure.
                this.props.onChange(matchingOption.key.toString(), true);
            } else if (this.props.selectedKey) {
                // If there's no matching option and we have a selected option, unselect that option.
                this.props.onChange(undefined);
            }
        }
    }

    private onRenderOption(
        option?: ISelectableOption,
        defaultRender?: (props?: ISelectableOption) => JSX.Element | null,
    ): JSX.Element | null {
        if (!option) {
            return null;
        }

        const debouncedSearchUpdate = new Async().debounce(this.updateSearch, 250);

        if (option.itemType === SelectableOptionMenuItemType.Header && option.key === "search") {
            return (
                <SearchBox
                    placeholder={store.i18n.t("component.searchableDropdown.search.placeholder")}
                    onChange={debouncedSearchUpdate}
                    onSearch={debouncedSearchUpdate}
                    onClear={debouncedSearchUpdate}
                />
            );
        }

        if (typeof this.props.onRenderOption === "function") {
            return this.props.onRenderOption(option, defaultRender);
        }

        if (!defaultRender) {
            return null;
        }

        return defaultRender(option);
    }

    private getOptions(): IDropdownOption[] {
        const result: IDropdownOption[] = [];

        if (!this.props.hideSearch) {
            result.push({
                key: "search",
                text: "",
                itemType: SelectableOptionMenuItemType.Header,
            });
        }

        return result.concat([...this.options]);
    }

    public render(): JSX.Element {
        const props = omit(
            ["repository", "query", "formatEntity", "onChange", "clearable"],
            this.props,
        );

        // Limit the width of the dropdown whenever we allow multiple
        // selections.
        if (this.props.multiSelect) {
            dropdownStyles.dropdown = { width: 200 };
        }

        return (
            <Stack horizontal>
                <Stack.Item style={{ width: "100%" }}>
                    <Dropdown
                        styles={dropdownStyles}
                        onChange={this.updateSelection}
                        options={this.getOptions()}
                        onDismiss={() => (this.currentSearch = undefined)}
                        onRenderOption={(
                            option?: ISelectableOption,
                            defaultRender?: (props?: ISelectableOption) => JSX.Element | null,
                        ): JSX.Element | null => {
                            return this.onRenderOption(option, defaultRender);
                        }}
                        {...props}
                    />
                </Stack.Item>
                {this.props.clearable && (
                    <Stack.Item align="end">
                        <IconButton
                            disabled={!this.props.selectedKey}
                            iconProps={{ iconName: "Cancel" }}
                            onClick={() => this.clearSelection(undefined, undefined)}
                        />
                    </Stack.Item>
                )}
            </Stack>
        );
    }
}
